import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Siren, Truck, PhoneCall, MapPin, CheckCircle2, Loader2 } from "lucide-react";

const TYPES = {
  accident_report: { label: "Report Accident", icon: Siren, color: "#EF4444" },
  towing: { label: "Towing Assistance", icon: Truck, color: "#FF7A00" },
  call_back: { label: "Call Claim Expert", icon: PhoneCall, color: "#003DA5" },
  find_garage: { label: "Find Nearby Garage", icon: MapPin, color: "#00A86B" },
};

export default function Emergency() {
  const [sp] = useSearchParams();
  const [type, setType] = useState(sp.get("type") || "accident_report");
  const [form, setForm] = useState({ name: "", mobile: "", location: "", description: "" });
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = sp.get("type");
    if (t && TYPES[t]) setType(t);
  }, [sp]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/emergency", { ...form, request_type: type });
      setResult(data);
    } catch (e) {
      alert("Could not send. Call 1800-000-000.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F8FAFC] py-10" data-testid="emergency-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="inline-block px-3 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-bold uppercase tracking-[0.2em] mb-3">
            🚨 Emergency Hub
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            We're on it. Tell us where.
          </h1>
          <p className="text-slate-600 mt-1">Our claim experts will call you within 5 minutes.</p>
        </div>

        {/* Type selector */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
          {Object.entries(TYPES).map(([k, v]) => {
            const Icon = v.icon;
            const active = k === type;
            return (
              <button
                key={k}
                onClick={() => setType(k)}
                className={`p-3 rounded-xl border text-center transition-all ${
                  active ? "border-transparent shadow-lg text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
                style={active ? { background: v.color } : {}}
                data-testid={`emergency-type-${k}`}
              >
                <Icon className="w-5 h-5 mx-auto mb-1" />
                <div className="text-xs font-bold">{v.label}</div>
              </button>
            );
          })}
        </div>

        {result ? (
          <div className="bg-white rounded-2xl border border-[#00A86B] p-8 text-center" data-testid="emergency-success">
            <CheckCircle2 className="w-14 h-14 text-[#00A86B] mx-auto mb-3" />
            <h2 className="font-heading font-bold text-2xl text-slate-900">Help is on the way!</h2>
            <p className="text-slate-600 mt-2">{result.message}</p>
            <div className="mt-4 inline-block px-4 py-2 rounded-lg bg-slate-50 font-mono font-bold text-slate-900" data-testid="emergency-request-id">
              {result.request_id}
            </div>
            <div className="mt-6">
              <Link to="/" className="text-[#003DA5] underline font-semibold text-sm">Back to home</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="bg-white border border-slate-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#003DA5] outline-none" data-testid="emergency-name" />
              <input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })} maxLength={10} placeholder="Mobile (10-digit)" className="bg-white border border-slate-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#003DA5] outline-none" data-testid="emergency-mobile" />
            </div>
            <input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location / nearest landmark" className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#003DA5] outline-none" data-testid="emergency-location" />
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Anything else we should know?" className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#003DA5] outline-none" data-testid="emergency-description" />
            <button
              disabled={submitting || !form.name || form.mobile.length < 10 || !form.location}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#FF7A00] hover:bg-[#CC6200] disabled:bg-slate-300 text-white rounded-xl px-5 py-3.5 font-semibold transition-colors"
              data-testid="emergency-submit"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send Help Request"}
            </button>
            <p className="text-xs text-center text-slate-500">Or call us directly: <a href="tel:+919942249388" className="font-bold text-[#003DA5]">+91 99422 49388</a></p>
          </form>
        )}
      </div>
    </div>
  );
}
